import { fetchGet, fetchPost } from "@/lib/services";
import toast from "react-hot-toast";

// Types for dashboard data
export interface DashboardStats {
  totalSubscriptions: number;
  newRegistrations: number;
  pendingVerifications: number;
  activeDeliveries: number;
  monthlyRevenue: number;
  verifiedUsers: number;
}

export interface RevenueDataPoint {
  month: string;
  revenue: number;
  subscriptions: number;
}
 
export interface RevenueAnalytics {
  data: RevenueDataPoint[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

export interface RecentUsersResponse {
  users: User[];
  total: number;
}

export interface LeaveRequest {
  id: string;
  userId: string;
  userName: string;
  reason: string;
  startDate: string;
  endDate: string;
  status: "pending" | "approved" | "rejected";
}

export interface LeaveRequestsResponse {
  requests: LeaveRequest[];
  total: number;
}

// Dashboard API calls
export const getDashboardStats = async (): Promise<DashboardStats | null> => {
  try {
    const response = await fetchGet("/api/admin/dashboard/stats");
    if (response.success) {
      return response.data as DashboardStats;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch dashboard stats:", error);
    return null;
  }
};

export const getRevenueAnalytics = async (): Promise<RevenueAnalytics | null> => {
  try {
    const response = await fetchGet("/api/admin/dashboard/revenue-analytics");
    if (response.success) {
      return response.data as RevenueAnalytics;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch revenue analytics:", error);
    return null;
  }
};

export const getRecentUsers = async (limit: number = 5): Promise<RecentUsersResponse | null> => {
  try {
    const response = await fetchGet(`/api/admin/users?limit=${limit}`);
    if (response.success) {
      return response.data as RecentUsersResponse;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch recent users:", error);
    return null;
  }
};

export const getLeaveRequests = async (status?: string): Promise<LeaveRequestsResponse | null> => {
  try {
    const url = status 
      ? `/api/admin/leave-requests?status=${status}` 
      : "/api/admin/leave-requests";
    const response = await fetchGet(url);
    if (response.success) {
      return response.data as LeaveRequestsResponse;
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch leave requests:", error);
    return null;
  }
};

export const approveLeaveRequest = async (id: string): Promise<boolean> => {
  try {
    const response = await fetchPost(`/api/admin/leave-requests/${id}/approve`, {});
    
    if (response.success) {
      toast.success('Leave request approved successfully!');
      return true;
    }
    
    toast.error(response.errors?.non_field_errors?.[0] || 'Failed to approve leave request');
    return false;
  } catch (error) {
    console.error("Failed to approve leave request:", error);
    toast.error('Something went wrong. Please try again.');
    return false;
  }
};

export const rejectLeaveRequest = async (id: string, reason: string): Promise<boolean> => {
  try {
    const response = await fetchPost(`/api/admin/leave-requests/${id}/reject`, { reason });

    if (response.success) {
      toast.success('Leave request rejected successfully!');
      return true;
    }

    toast.error(response.errors?.non_field_errors?.[0] || 'Failed to reject leave request');
    return false;
  } catch (error) {
    console.error("Failed to reject leave request:", error);
    toast.error('Something went wrong. Please try again.');
    return false;
  }
};

export const getLunchUpdates = async () => {
  return await fetchGet("/lunchupdate/");
};

export async function getDashboardData(
  params: Record<string, string> = {}
) {
  const query = new URLSearchParams(params).toString();

  return await fetchGet(
    `/dashboard/overview/${query ? `?${query}` : ""}`
  );
}
